import { useQuery } from "@tanstack/react-query";
import { ChartWrapper, LineChart, getDateFormatByCount } from "components/charts";
import ReportingFilter from "components/reportingFilter/ReportingFilter";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import {
  type QueryFilter,
  isUserDeleted,
  formatUserName,
  lineChartFactory,
  noData,
  validLineData,
  createDateRange,
  canUseChildAccount,
  type PerformanceWithAccountFilter,
} from "features/Library/Common/utils/performanceUtils";
import { sharedAccountReportingLineProps } from "features/Reporting/Content/shared";
import { GenericPerformanceListWithServerPagination } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { peopleReportingService } from "features/Reporting/services/peopleReportingService";
import { type ColumnOption } from "interfaces/columnOptions";
import { useCallback, useMemo } from "react";
import dateTimeUtils from "utils/dateTimeUtils";
import CardReporting, { type CardReportingItem } from "components/cards/CardReporting/CardReporting";

import "features/Library/Common/utils/performanceSCSSUtils.scss";
import "../peopleStyles.scss";
import { Tooltip } from "components/common/tooltip";
import { TextTruncate } from "components";
import { MoboLink } from "components/MoboLink/MoboLink";
import { RouteNames } from "enums";
import { CHART_PERIOD, useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";
import { usePerformanceFilter } from "features/Library/Common/Hooks/usePerformanceFilter";
import { useAccountFilterShouldShow } from "features/Reporting/Content/queries/useAccountFilterShouldShow";
import { type RootState } from "features/Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";
import { type PaginationParams } from "features/Library/Common/models";
import { type FetchPageResult } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";

const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;

const getPeopleLineChart = async ({ queryKey, signal }: QueryFilter) => {
  const result = await peopleReportingService.users.getLineChart(queryKey[1], signal!);

  return lineChartFactory(result.data);
};

const getPeopleCards = async ({ queryKey, signal }: QueryFilter): Promise<CardReportingItem[]> => {
  const { data } = await peopleReportingService.users.getStatistics(queryKey[1], signal!);

  return [
    {
      stat: data.Users,
      popupBody: "The total number of users for this account within within the specified date range.",
      statDescription: "Users",
    },
    {
      stat: data.Departments,
      popupBody: "The total number of departments for this account within the specified date range.",
      statDescription: "Departments",
    },
    {
      stat: data.Groups,
      popupBody: "The total number of groups for this account within the specified date range.",
      statDescription: "Groups",
    },
    {
      stat: data.Licenses,
      popupBody: "The total number of licenses granted to users of this account within the specified date range.",
      statDescription: "Licenses",
    },
  ];
};

type Re = {
  FirstName: string | null;
  LastName: string | null;
  id: number | null;
  platformOutreach: number;
  userInteractions: number;
  lastActivity: string;
};

type ReColumnOption = ColumnOption<Re> & { sortKey?: string };

const fetchPeopleTable = async (
  filter: PerformanceWithAccountFilter,
  pagination: PaginationParams,
  signal?: AbortSignal,
): Promise<FetchPageResult<Re>> => {
  const response = await peopleReportingService.users.getTable(filter, pagination, signal);

  const rows = response.data.map((r) => ({
    FirstName: r.FirstName,
    LastName: r.LastName,
    id: r.UserId,
    platformOutreach: r.PlatformOutreach,
    userInteractions: r.PlatformInteraction,
    lastActivity: r.LastActivity ? dateTimeUtils.formatDate(r.LastActivity) : "-",
  }));

  return {
    rows,
    totalCount: Number.parseInt(response.headers[countHeaderName]),
  };
};

type UsersReportProps = {
  usesDropdownMenuNavigation?: boolean;
  renderHeader?: () => JSX.Element | null;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
} & PropsFromRedux;

const UsersReport = ({
  usesDropdownMenuNavigation,
  renderHeader,
  childAccountId,
  hasCreateAccountsAddOn,
  accountId,
  accountType,
}: UsersReportProps) => {
  const initialOverrides = useMemo(() => {
    if (usesDropdownMenuNavigation && childAccountId) {
      return { accounts: [childAccountId] as number[], includeMyData: false };
    }
    return undefined;
  }, [usesDropdownMenuNavigation, childAccountId]);
  const accountFilterShouldShow = useAccountFilterShouldShow(accountId, accountType);
  const [filter, setFilter] = usePerformanceFilter({
    accountFilterShouldShow: false,
    includeAccountsDropdown: accountFilterShouldShow,
    supportsSPType: true,
    accountId,
    initialOverrides,
  });

  const queryKey = useMemo(() => ["people users table query", filter] as const, [filter]);

  const fetchTableData = useCallback(
    (params: PaginationParams, signal?: AbortSignal) => fetchPeopleTable(filter, params, signal),
    [filter],
  );

  const columns: ReColumnOption[] = useMemo(
    () => [
      {
        name: "Name",
        width: 7,
        isSortable: true,
        sortKey: "FirstName",
        render(item) {
          const formattedName = formatUserName(item.FirstName, item.LastName);
          const includeAccounts = accountFilterShouldShow && (filter.accounts?.length ?? 0) > 0;
          const childAccountEnabled = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);
          const shouldShowLink =
            !isUserDeleted(item.FirstName, item.LastName) && (!includeAccounts || childAccountEnabled);

          return (
            <Tooltip
              target={
                <TextTruncate>
                  {shouldShowLink ? (
                    <MoboLink
                      target={childAccountEnabled ? "_blank" : undefined}
                      accountId={childAccountEnabled ? childAccountId : undefined}
                      to={`/${RouteNames.peopleUsers}/${item.id}`}
                    >
                      {formattedName}
                    </MoboLink>
                  ) : (
                    formattedName
                  )}
                </TextTruncate>
              }
              content={formattedName}
            />
          );
        },
      },
      {
        name: "Platform Outreach",
        width: 7,
        isSortable: true,
        sortKey: "PlatformOutreach",
        render(item) {
          return <span>{(item.platformOutreach ?? 0).toLocaleString()}</span>;
        },
      },
      {
        name: "Platform Interactions",
        width: 7,
        isSortable: true,
        sortKey: "PlatformInteraction",
        render(item) {
          return <span>{(item.userInteractions ?? 0).toLocaleString()}</span>;
        },
      },
      {
        name: "Last Activity",
        width: 7,
        isSortable: true,
        sortKey: "LastActivity",
        render(item) {
          return <span>{item.lastActivity}</span>;
        },
      },
    ],
    [childAccountId, accountFilterShouldShow, filter.accounts, hasCreateAccountsAddOn],
  );

  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const lineChartQuery = useQuery({
    queryKey: ["people users line query", { ...filter, aggregation: chartPeriod }],
    queryFn: getPeopleLineChart,
    enabled: chartPeriod !== CHART_PERIOD.UNSET,
  });

  const cardQuery = useQuery({
    queryKey: ["people users cards", filter],
    queryFn: getPeopleCards,
  });

  const ignoreInCountAndHide = useMemo(() => {
    const ignore = ["type"];
    if (usesDropdownMenuNavigation) {
      ignore.push("accounts", "includeMyData");
    }
    return ignore;
  }, [usesDropdownMenuNavigation]);

  return (
    <div className="performanceRoot">
      <div className="performanceHeader peopleActivityHeader">
        {usesDropdownMenuNavigation && renderHeader ? renderHeader() : <h2 className="performanceTitle">Summary</h2>}
        <div>
          <ReportingFilter
            currentFilter={filter}
            callback={setFilter}
            includeAccounts={false}
            supportsSPType
            includeAccountsDropdown={accountFilterShouldShow}
            ignoreInCountAndHide={ignoreInCountAndHide}
          />
        </div>
      </div>
      <div className="performanceBody">
        <div className="lineChartContainer" ref={measureRef}>
          <ChartWrapper titles={["Active Users"]}>
            <RequestStatusRenderer state={lineChartQuery.status}>
              {lineChartQuery.isSuccess && validLineData([lineChartQuery.data.UserCount]) ? (
                <LineChart
                  {...sharedAccountReportingLineProps}
                  xData={[lineChartQuery.data.Date]}
                  yData={[lineChartQuery.data.UserCount]}
                  xFormatterFunc={getDateFormatByCount(lineChartQuery.data.Date.length)}
                />
              ) : (
                noData(filter)
              )}
            </RequestStatusRenderer>
          </ChartWrapper>
        </div>
        <div className="cards">
          <RequestStatusRenderer state={cardQuery.status}>
            <CardReporting items={cardQuery.data!} />
          </RequestStatusRenderer>
        </div>
        <GenericPerformanceListWithServerPagination
          title="People"
          columns={columns}
          filter={filter}
          fetchPage={fetchTableData}
          queryKeyBase={queryKey}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
  accountType: state.userProfile.accountTypeId,
});

const connector = connect(mapStateToProps, null);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(UsersReport);
export default ConnectedComponent;
